<template>
	<div>
		<div class="d-flex justify-content-between">
			<h1 class="page-header">{{ $t('businessUnit.pageTitle') }}</h1>
			<ts-breadcrumb :items="[
				{ text: $t('home'), href: '/' },
				{
					text: $t('businessUnit.pageTitle'),
					active: true
				}
			]" />
		</div>
		<ts-card>
			<ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-space-x-3">
            <ts-button outline color="info" @click.prevent="showForm">
              {{ $t('add_new') }}
            </ts-button>
          </div>
          <div class="d-flex">
            <a-input-search @search="onSearch"
              v-tooltip="$t('businessUnit.search')"
              :placeholder="$t('businessUnit.search')"
              style="width: 250px;"
            />
          </div>
        </div>
			</ts-card-wrapper>
			<div class="overflow-auto">
				<Table :data="resources" :columns="columns" size="small" highlight-row stripe max-height="700"
					:loading="fetching">

					<template slot-scope="{ row }" slot="action">
						<a href="#" v-tooltip="$t('edit')" class="text-primary mr-2 ml-2" @click.prevent="setEdit(row)">
							<i class="fas fa-edit"></i>
						</a>
						<a-popconfirm placement="leftTop" :title="$t('are_you_sure_to_delete')" :ok-text="$t('yes')"
							:cancel-text="$t('no')" @confirm="onDelete(row)">
							<a href="#" :disabled="row._deleting">
								<i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
								<i class="fa fa-trash text-red" v-else></i>
							</a>
						</a-popconfirm>
					</template>
				</Table>
			</div>
			<div class="d-flex justify-content-between align-items-center p-15">
				<ts-total-list v-model="pagination" />
				<ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
			</div>
		</ts-card>

		<a-modal :title="$t('businessUnit.pageTitle')" v-if="show_form" :visible="show_form" @cancel="onCancel"
			width="450px" :footer="null" :maskClosable="false" :centered="true">
			<ts-loading-banner :loading="loading">
				<div class="row">
					<div class="col-md-12">
						<label class="required">{{ $t('businessUnit.displayName') }}</label>
						<a-input v-model="model.display_name" class="ts-w-full"></a-input>
						<div class="ts-text-red-500 ts-text-xs" v-if="errors.has('display_name')">{{ errors.first('display_name')
						}}</div>
					</div>
				</div>
				<div class="row">
					<div class="col-md-12 ts-mt-3 text-right ts-space-x-2 ts-flex ts-justify-end">
						<ts-button class="mr-2" @click="onCancel">
							{{ $t('cancel') }}
						</ts-button>
						<ts-button v-if="!isUpdate" color="primary" outline @click="onSaveAddNew">{{ $t('saveAddNew') }}</ts-button>
						<ts-button v-if="!isUpdate" color="primary" @click="onSave">{{ $t('save') }}</ts-button>
						<ts-button v-if="isUpdate" color="primary" @click="onUpdate">{{ $t('update') }}</ts-button>
					</div>
				</div>
			</ts-loading-banner>
		</a-modal>
	</div>
</template>

<script>

import { Errors } from 'form-backend-validation';
import { mapActions, mapState } from 'vuex';

export default {
	name: 'businessUnit',
	data() {
		return {
			loading: false,
			fetching: false,
			show_form: false,
			errors: new Errors(),
			model: {
				id: undefined,
				display_name: ''
			}
		}
	},
	mounted() {
		this.fetchData()
	},
	computed: {
		...mapState('product/businessUnit', ['resources', 'pagination']),
		isUpdate() {
			return this.model.id !== undefined
		},
		columns() {
			return [
				{
					title: this.$t('businessUnit.displayName'),
					key: 'display_name',
					whiteSpace: 'nowrap',
					minWidth: 200
				},
				{
					title: this.$t('businessUnit.createdBy'),
					width: 200,
					key: 'created_name'
				},
				{
					title: this.$t('businessUnit.updatedBy'),
					width: 200,
					key: 'updated_name'
				},
				{
					title: this.$t('businessUnit.createdAt'),
				 	width: 200, 
					key: 'created_at'
				},
				{
					title: this.$t('businessUnit.updatedAt'),
				 	width: 200, 
					key: 'updated_at'
				},
				{
					title: this.$t('actions'),
					key: 'action',
					slot: 'action',
					fixed: 'right',
					align: 'center',
					width: 120
				}
			]
		}
	},
	methods: {
		...mapActions('product/businessUnit', [
			'fetch',
			'store',
			'update',
			'destroy',
		]),

		showForm() {
			this.show_form = true
		},
		onCancel() {
			this.show_form = false
			this.model.id = undefined
			this.model.display_name = ''
			this.fetchData()
		},
		fetchData(attributes) {
			this.fetching = true
			this.fetch({ ...attributes })
				.catch((error) => {
					this.$toasted.error(error.message)
				})
				.finally(() => this.fetching = false)
		},
		onSave() {
			this.loading = true
			this.store(this.model)
				.then(response => {
					this.$toasted.success(response.message)
					this.onCancel()
				})
				.catch(error => {
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => {
					this.loading = false
				})
		},
		onSaveAddNew() {
			this.loading = true
			this.store(this.model)
				.then(response => {
					this.$toasted.success(response.message)
					this.clearInput()
				})
				.catch(error => {
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => {
					this.loading = false
				})
		},
		onUpdate() {
			this.loading = true
			this.update({
				id: this.model.id,
				data: this.model
			})
				.then(response => {
					this.$toasted.success(response.message)
				})
				.catch(error => {
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => {
					this.loading = false
				})
		},
		onDelete(record) {
			record._deleting = true
			this.destroy(record.id)
				.then(response => {
					this.$toasted.success(response.message)
					this.fetchData()
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					record._deleting = false
				})
		},
		clearInput() {
			this.model.id = undefined
			this.model.display_name = ''
		},
		setEdit(record) {
			this.model.id = record.id
			this.model.display_name = record.display_name
			this.show_form = true
		},
		onSearch(value){
			this.fetchData({
				filter: {
					search: value
				}
			})
		}
	}
}
</script>